/** Week-window helpers for class upcoming dates. */

export function startOfLocalDay(d = new Date()) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Inclusive window: [anchor, anchor+6d] end-of-day. */
export function weekWindow(anchorDate) {
  const start = startOfLocalDay(anchorDate);
  const end = addDays(start, 7);
  return { start, end };
}

export function isUpcomingInstance(inst, nowMs = Date.now()) {
  if (!inst || inst.status === "CANCELLED") return false;
  const start = inst.effective_datetime || inst.datetime;
  if (!start) return false;
  return new Date(start).getTime() >= nowMs;
}

export function instancesInWeek(instances, anchorDate, nowMs = Date.now()) {
  const { start, end } = weekWindow(anchorDate);
  return (instances || []).filter((inst) => {
    if (!isUpcomingInstance(inst, nowMs) && new Date(inst.effective_datetime || inst.datetime).getTime() < start.getTime()) {
      // allow past-in-week only when browsing previous weeks relative to now
    }
    const t = new Date(inst.effective_datetime || inst.datetime).getTime();
    if (Number.isNaN(t)) return false;
    if (inst.status === "CANCELLED") return false;
    return t >= start.getTime() && t < end.getTime();
  });
}

/** For default "this week": only future sessions within next 7 days from now. */
export function upcomingInNextWeek(instances, nowMs = Date.now()) {
  const start = nowMs;
  const end = start + 7 * 86400000;
  return (instances || []).filter((inst) => {
    if (!isUpcomingInstance(inst, nowMs)) return false;
    const t = new Date(inst.effective_datetime || inst.datetime).getTime();
    return t < end;
  });
}

export function shiftWeekAnchor(anchorDate, deltaWeeks) {
  return addDays(startOfLocalDay(anchorDate), deltaWeeks * 7);
}

export function resolveWhatsAppUrl(classDoc, school) {
  return classDoc?.whatsapp_url || school?.whatsapp_url || null;
}

export function buildClassSessionIcs({ title, description, startIso, endIso, location }) {
  const stamp = (iso) => {
    const d = new Date(iso);
    return d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
  };
  const uid = `class-${Date.now()}@rsvp.dance`;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//rsvp.dance//class//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${stamp(new Date().toISOString())}`,
    `DTSTART:${stamp(startIso)}`,
    endIso ? `DTEND:${stamp(endIso)}` : null,
    `SUMMARY:${escapeIcs(title || "Class")}`,
    description ? `DESCRIPTION:${escapeIcs(description)}` : null,
    location ? `LOCATION:${escapeIcs(location)}` : null,
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean);
  return lines.join("\r\n");
}

function escapeIcs(text) {
  return String(text).replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}

export function downloadIcs(filename, content) {
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "class.ics";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const VENUE_TBD_KEY = "__tbd__";
const VENUE_TBD_LABEL = "Location not set — ask host";

/**
 * Resolve venue for a class instance from nested `venue` / `effective_venue`,
 * `effective_venue_id`, or a lookup map of school venues.
 */
export function resolveInstanceVenue(inst, venueById = {}) {
  if (inst?.effective_venue?.id) return inst.effective_venue;
  if (inst?.venue?.id) return inst.venue;
  const id = inst?.effective_venue_id || inst?.custom_venue_id || null;
  if (id && venueById[id]) return venueById[id];
  return null;
}

/** Street text vs map URL for venue secondary line under the group header. */
export function formatVenueSecondaryLine(addressOrLink) {
  const raw = String(addressOrLink || "").trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) {
    return { kind: "map", href: raw, label: "Open map" };
  }
  if (/^www\./i.test(raw)) {
    return { kind: "map", href: `https://${raw}`, label: "Open map" };
  }
  return { kind: "text", label: raw };
}

/**
 * Group upcoming class instances by venue so students can pick a location.
 * Groups are ordered by earliest session; unset location last.
 * Returns a single untitled group when every instance shares one venue (or none).
 *
 * @returns {{ key: string, venue: object|null, label: string, address: string|null, instances: object[] }[]}
 */
export function groupInstancesByVenue(instances, venues = []) {
  const list = instances || [];
  if (!list.length) return [];

  const venueById = Object.fromEntries(
    (venues || []).filter((v) => v?.id).map((v) => [String(v.id), v]),
  );

  const buckets = new Map();
  for (const inst of list) {
    const venue = resolveInstanceVenue(inst, venueById);
    const key = venue?.id ? String(venue.id) : VENUE_TBD_KEY;
    let group = buckets.get(key);
    if (!group) {
      group = {
        key,
        venue: venue || null,
        label: venue?.name || VENUE_TBD_LABEL,
        address: venue?.address_or_link || null,
        instances: [],
      };
      buckets.set(key, group);
    }
    group.instances.push(inst);
  }

  const groups = [...buckets.values()];
  groups.sort((a, b) => {
    if (a.key === VENUE_TBD_KEY) return 1;
    if (b.key === VENUE_TBD_KEY) return -1;
    const aStart = a.instances[0]?.effective_datetime || a.instances[0]?.datetime || "";
    const bStart = b.instances[0]?.effective_datetime || b.instances[0]?.datetime || "";
    return String(aStart).localeCompare(String(bStart));
  });

  return groups;
}

/** True when the list spans more than one venue (worth showing venue headers). */
export function shouldShowVenueGroups(groups) {
  return (groups || []).length > 1;
}
